<i18n src="./resources/locales.json"></i18n>

<template>
  <section
    class="window-area"
    :class="{
      'footer-attached-file': hasAttachedFile,
      'footer-attached-gif': hasGifFile
    }"
  >
    <!-- Headers -->
    <SmsNewMessageHeader v-show="isNewMessage" ref="newConversationHeader" class="grid-header"
                         :newMessageGroupMode="newMessageGroupMode" />
    <SmsConversationHeader v-show="!isNewMessage" ref="conversationHeader" class="grid-header" />
    <v-progress-linear v-show="isLoading" ref="loadingBar" class="grid-loading-bar" indeterminate height="3" />

    <!-- Messages / Body -->
    <div ref="messagesContainer" class="grid-messages custom-scrollbar" @scroll="onScroll">
      <div ref="messages" class="messages">
        <div v-for="(message, index) in messages" :key="index">
          <div class="sms-divider" v-if="timeAgoDivider(index)">
            <div class="sms-divider-text">{{ timeAgoDivider(index) }}</div>
          </div>
          <SmsMessage :message="message" @onContextMenu="messageContextMenuHandler" />
        </div>
        <v-menu
          v-model="contextMenu.show"
          :position-x="contextMenu.position.x"
          :position-y="contextMenu.position.y"
          absolute
          offset-y
        >
          <v-list>
            <v-list-item v-if="canCopyMessage(contextMenu.selectedMessage)" @click="copyMessage">
              <v-list-item-icon>
                <v-icon>mdi-content-copy</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('messages.copyText') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="canDownloadMessage(contextMenu.selectedMessage)" @click="downloadImage">
              <v-list-item-icon>
                <v-icon>mdi-download</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('messages.downloadImage') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if='canDeleteMessage(contextMenu.selectedMessage)'
                         @click='deleteMessage'>
              <v-list-item-icon>
                <v-icon>mdi-delete</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('messages.deleteMessage') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- div v-show="newMessagesAvailable" class="new-messages-container">
      <v-btn color="var(--v-primary-base)" rounded dark @click="scrollDown">
        <span>New messages</span>
      </v-btn>
    </div -->

    <SmsConversationFooter
      ref="conversationFooter"
      class="grid-footer"
      v-show="showFooter"
      :changeAttachedContent="changeHasAttached"
      :newMessageGroupMode="newMessageGroupMode"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import debounce from 'lodash/debounce';
import SmsMessage from '@/components/SmsMessage';
import SmsConversationHeader from '@/components/SmsConversation/SmsConversationHeader';
import SmsNewMessageHeader from '@/components/SmsConversation/SmsNewMessageHeader';
import SmsConversationFooter from '@/components/SmsConversation/SmsConversationFooter';
import ValidationMixin from '@/mixins/ValidationMixin';
import ToastMixin from '@/mixins/ToastMixin';

export default {
  name: 'SmsConversationWindow',

  components: { SmsMessage, SmsConversationHeader, SmsNewMessageHeader, SmsConversationFooter },

  mixins: [ValidationMixin, ToastMixin],

  data() {
    return {
      messageText: '',
      isLoading: false,
      newMessagesAvailable: false,
      prevHeight: 0,
      maxHistoricalMessagesReached: false,

      contactSelectionOpen: false,
      hasAttachedFile: false,
      hasGifFile: false,
      newMessageGroupMode: false,

      headerResizeObserver: null,
      footerResizeObserver: null,

      contextMenu: {
        show: false,
        selectedMessage: null,
        position: {
          x: 0,
          y: 0
        }
      }
    };
  },

  computed: {
    ...mapState('user', { userId: state => state.userId, activeNumber: state => state.activeNumber }),
    ...mapGetters('conversationsV2', ['activeConversation', 'activeConversationId', 'allConversations', 'tmpMessages']),

    isNewMessage() {
      return Boolean(this.activeConversationId) === false;
    },

    messages() {
      return this.activeConversation?.sortedMessages || this.tmpMessages || [];
    },

    showFooter() {
      return this.activeConversation || this.isNewMessage;
    }
  },

  watch: {
    async 'activeConversation.newestMessage.originationTime'() {
      await this.$nextTick();
      this.scrollDown();
    },
    async 'activeConversation.oldestMessage.originationTime'() {
      await this.$nextTick();
      const dividerHeight = 46;
      this.$refs.messagesContainer.scrollTop =
        this.$refs.messagesContainer.scrollHeight - this.prevHeight + dividerHeight;
    },
    activeConversationId(number) {
      if (number) this.loadMessagesDebounced();
      this.maxHistoricalMessagesReached = false;
      this.prevHeight = 0;
    },
    isNewMessage() {
      this.setupListeners();
    }
  },

  created() {
    this.$root.$on('change-conversation', this.changeConversation);
    this.$root.$on('update-draft-message', this.updateDraftMessage);
    this.$root.$on('toggle-group-message-mode', this.toggleGroupMessageMode);
    this.loadMessages().then(async () => {
      await this.$nextTick();
      this.scrollDown();
      this.loadVisibleAttachments();
    });
  },

  beforeDestroy() {
    delete this.headerResizeObserver;
    delete this.footerResizeObserver;
    this.$root.$off('update-draft-message');
    this.$root.$off('toggle-group-message-mode');
  },

  methods: {
    ...mapActions('conversationsV2', ['retrieveConversationByNumber', 'setActiveConversationId', 'deleteMessageByRowId']),

    messageContextMenuHandler({ event, message }) {
      const clientRect = event.target.getBoundingClientRect();
      this.contextMenu.position.x = clientRect.x;
      this.contextMenu.position.y = clientRect.y;
      this.contextMenu.show = true;
      this.contextMenu.selectedMessage = message;
    },

    toggleGroupMessageMode(val) {
      if(val || val === false){
        this.newMessageGroupMode = val;
      } else {
        this.newMessageGroupMode = !this.newMessageGroupMode;
      }
    },

    canCopyMessage(message) {
      return !!message?.body;
    },

    copyMessage() {
      navigator.clipboard.writeText(this.contextMenu.selectedMessage.body);
    },

    deleteMessage() {
      const selectedMessage = this.contextMenu.selectedMessage;
      const rowId = selectedMessage?.rowId;
      this.deleteMessageByRowId({ rowId }).then(response=> {
        if (!response.successful) {
          console.error(`Failed to delete message by row id: ${rowId}.`);
          this.showError(response.responseMessage);
        }
        else {
          this.showSuccess(response.responseMessage);
        }
      });
    },

    canDeleteMessage(message) {
      //TODO: hide delete message for now, but need to fix this in the future
      if (!message || true) return false;

      return message.rowId !== undefined && message.rowId !== null;
    },

    canDownloadMessage(message) {
      return !!message?.attachments;
    },

    downloadImage() {
      const file = this.contextMenu.selectedMessage.attachments[0];
      const a = document.createElement('a');
      a.href = `data:${file.contentType};base64,${file.content}`;
      a.download = `file.${file.contentType !== 'plain' ? file.contentType.replace(/^\w+\//, '') : 'txt'}`;
      a.click();
    },

    timeAgoDivider(index) {
      const currentMessage = this.$moment(this.messages[index].originationTime).endOf('day');
      if (index === 0) return currentMessage.format('MMM, DD YYYY');

      const lastMessage = this.$moment(this.messages[index - 1].originationTime);
      const messagesDiff = lastMessage.endOf('day').diff(currentMessage, 'days');
      if (messagesDiff === 0) return null;

      const fromTodayDiff = this.$moment().endOf('day').diff(currentMessage.endOf('day'), 'days');

      if (fromTodayDiff === 0) return this.$t('messages.today');
      if (fromTodayDiff === 1) return this.$t('messages.yesterday');
      return currentMessage.format('MMM, DD YYYY').replace('.', '');
    },

    /**
     * Scrolls conversation container to the bottom.
     **/
    scrollDown() {
      this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer?.scrollHeight;
      this.loadVisibleAttachments();
    },

    changeHasAttached(attached) {
      this.hasAttachedFile = attached.hasFile;
      this.hasGifFile = attached.hasGif;
    },

    changeConversation() {
      if (this.activeConversation) {
        this.$store.dispatch('conversations/updateDraftMessage', {
          conversationId: this.activeConversationId,
          draftMessage: this.$refs.conversationFooter.$refs.textMessageInput.messageText
        });
      }
    },

    loadMessagesDebounced: debounce(function () {
      this.loadMessages();
    }, 200),

    loadMessages() {
      if (!this.activeConversation?.contact.number) return Promise.resolve();
      if (this.maxHistoricalMessagesReached) return Promise.resolve();

      this.isLoading = true;
      // NOTE: We are using the conversation id as the number to retrieve by
      return this.retrieveConversationByNumber({
        number: this.activeConversation.conversationId,
        until: this.activeConversation.headerOriginationTime
      })
        .then(response => {
          if (response === null) {
            this.maxHistoricalMessagesReached = true;
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    loadVisibleAttachments() {
      const messages = this.$refs.messages.querySelectorAll('.conversation-message');
      const visibleMessages = [...messages].filter(a => a.getBoundingClientRect().top > 0);
      visibleMessages.forEach(message => message.__vue__.loadAttachments());
    },

    onScroll({ target }) {
      if (target.scrollTop === 0) {
        this.prevHeight = target.scrollHeight;
        this.loadMessagesDebounced();
      }
    },

    onResize() {
      const DEFAULT_HEADER_HEIGHT = 60;
      const header = this.isNewMessage ? this.$refs.newConversationHeader : this.$refs.conversationHeader;
      const headerHeight = !header || !header.$el || !header.$el.getBoundingClientRect ? DEFAULT_HEADER_HEIGHT : header.$el.getBoundingClientRect().height;
      const footerHeight = this.$refs.conversationFooter.$el.getBoundingClientRect().height;
      this.$refs.loadingBar.$el.style.top = `${headerHeight}px`;
      this.$refs.messagesContainer.style.height = `${document.body.clientHeight - headerHeight - footerHeight}px`;
      this.$refs.messagesContainer.style.top = `${headerHeight}px`;
      this.$refs.conversationFooter.$el.style.top =
        `${this.$refs.messagesContainer.getBoundingClientRect().bottom}px`;
    },

    async setupListeners() {
      await this.$nextTick();
      const header = this.isNewMessage ? this.$refs.newConversationHeader : this.$refs.conversationHeader;
      const footer = this.$refs.conversationFooter;
      this.headerResizeObserver = new ResizeObserver(this.onResize).observe(header.$el);
      this.footerResizeObserver = new ResizeObserver(this.onResize).observe(footer.$el);
    },

    goToMessage() {
      // TODO Review.
      // const message = this.$store.getters['filters/getMessages'].
      //   find(number => (number.phoneNumberContact === this.activeConversation.participants[0].number));
      // if(message) {
      // item.classList.add('change-color');
      // setTimeout(() => {
      //   item.classList.remove('change-color');
      // }, 6000);
      // }
    }
  },

  mounted() {
    this.setupListeners();
    this.$root.$on('go-search-message', this.goToMessage);
  }
};
</script>

<style src="./scoped.scss" lang="scss" scoped />
